import { siteApiSlice } from "../../app/siteApiSlice";
import NamedEntity from "../../interfaces/NamedEntity";

export interface GetSchoolsRequestModel {
	districtCode: string;
}

export interface SelectDistrictViewModel {
	districts: SelectDistrictRowSpaViewModel[];
	alerts: Record<number, string>;
}

export interface SelectDistrictRowSpaViewModel {
	id: number;
	name: string;
	schoolCount: number;
	code: string;
	cleverId: string;
	classLinkId: string;
}

export interface DistrictRowSpaViewModel {
	districtID: number;
	districtCode: string;
	districtName: string;
	city: string;
	state: string;
	emailAddress: string;
	phone: string;
	externalID: string;
	schoolCount: number;
	cleverID: string;
	classLinkID: string;
}

export interface DistrictCreateEditModel {
	id: number;
	name: string;
	/** The zone's code, e.g. "UT".  Read only for the SPA, which posts zoneId instead. */
	state: string;
	zoneId?: number;
	countryId?: number;
	address: string;
	address2: string;
	city: string;
	zip: string;
	contactName: string;
	phone: string;
	code: string;
	emailAddress: string;
	externalID: string;
	cleverID: string;
	showCleverId: boolean;
	classLinkID: string;
	showClassLinkId: boolean;
	disableUserImages: boolean;
	isIncludedInReports: boolean;
	takenSeats: number;
	totalSeats: number;
}

export interface DistrictSchoolListQueryModel {
	districtId: number;
	active: string;
}

export interface DistrictSchoolsResult {
	totalSeatsInUse: number;
	totalSeatsAllocated: number;
	schools: DistrictSchoolListViewModel[];
}

export interface DistrictSchoolListViewModel {
	id: number;
	externalID: string;
	name: string;
	active: boolean;
	expirationDate: Date;
	hasExpired: boolean;
	contact: string;
	phone: string;
	numCourses: number;
	seatsInUse: number;
	seatsAllocated: number;
	isCleverSchool: boolean;
	isClassLinkSchool: boolean;
}

export interface DistrictAdminViewModel {
	id: number;
	userName: string;
	formalFullName: string;
	phoneNumber: string;
	email: string;
	initials: string;
	hasImage: boolean;
	lastLoginDate: Date;
	disabled: boolean;
}

export interface AddRemoveUserToDistrictViewModel {
	districtId: number;
	userId: number;
}

export interface DistrictCleverInfoResult {
	cleverId: string;
	lastSyncAt: Date;
}

export interface UpdateDistrictCleverSettingsViewModel {
	districtId: number;
	cleverId: string;
}

export interface DistrictClassLinkInfoResult {
	tenantId: string;
	classLinkId: string;
	clientId: string;
	clientSecret: string;
	endpointUrl: string;
	syncStatus: string;
	syncStartTime: Date;
	syncFinishTime: Date;
}

export interface UpdateDistrictClassLinkSettingsViewModel {
	districtId: number;
	tenantId: string;
	classLinkID: string;
	clientId: string;
	clientSecret: string;
	endpointUrl: string;
	syncStatus: string;
}

export const districtsApiSlice = siteApiSlice
	.enhanceEndpoints({
		addTagTypes: ["DistrictAdmins", "CleverSettings", "ClassLinkSettings"],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			getDistricts: build.query<NamedEntity[], boolean | void>({
				query: (allowNullDistrict = false) => ({
					url: `/District/GetDistricts`,
					params: { allowNullDistrict },
				}),
				providesTags: ["Districts"],
			}),
			getSchools: build.mutation<NamedEntity[], GetSchoolsRequestModel>({
				query: (payload) => ({
					url: `/District/GetSchools`,
					method: "POST",
					body: payload,
				}),
			}),
			getSchoolsByDistrictId: build.mutation<
				NamedEntity[],
				{ districtId?: number; filterInactive: boolean }
			>({
				query: (payload) => {
					return {
						url: `/District/GetSchoolsByDistrictId`,
						method: "POST",
						body: payload,
					};
				},
			}),
			getDistrictPickerOptions: build.query<NamedEntity[], void>({
				query: () => ({
					url: `/District/DistrictPickerOptions`,
				}),
			}),
			createDistrict: build.mutation<number, DistrictCreateEditModel>({
				query: (data) => {
					return {
						url: `/District/Create`,
						method: "POST",
						body: data,
					};
				},
				invalidatesTags: ["Districts"],
			}),
			getDistrictInfo: build.query<DistrictCreateEditModel, number>({
				query: (id) => ({
					url: `/District/Edit`,
					params: { id },
				}),
				providesTags: ["District", "SeatCount"],
			}),
			editDistrict: build.mutation<void, DistrictCreateEditModel>({
				query: (data) => {
					return {
						url: `/District/Edit`,
						method: "POST",
						body: data,
					};
				},
				invalidatesTags: ["Districts", "District", "UserInfo"],
			}),
			getSelectDistrictOptions: build.query<SelectDistrictViewModel, void>({
				query: () => ({
					url: `/SignIn/SelectDistrict`,
				}),
				providesTags: ["Alerts"],
			}),
			makeDistrictSelection: build.mutation<void, number>({
				query: (districtId) => ({
					url: `/SignIn/MakeDistrictSelection/${districtId}`,
					method: "GET",
				}),
				invalidatesTags: ["CurrentDistrict"],
			}),
			getDistrictList: build.query<DistrictRowSpaViewModel[], void>({
				query: () => ({
					url: `/District/List`,
					method: "GET",
				}),
				providesTags: ["Districts"],
			}),
			updateUserAvatarSettingForDistrict: build.mutation<
				void,
				{ districtId: number; disabled: boolean }
			>({
				query: (payload) => {
					return {
						url: `/District/UpdateAvatarSettingForDistrict`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["District"],
			}),
			updateReportSettingForDistrict: build.mutation<
				void,
				{ districtId: number; enabled: boolean }
			>({
				query: (payload) => {
					return {
						url: `/District/UpdateReportSettingForDistrict`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["District"],
			}),
			schoolListByDistrict: build.query<DistrictSchoolsResult, DistrictSchoolListQueryModel>({
				query: (q) => {
					const { districtId, active: active } = q;
					return {
						url: `/District/SchoolListByDistrict`,
						params: {
							districtId,
							active,
						},
					};
				},
				providesTags: ["Schools", "SeatCount"],
			}),
			adminListByDistrict: build.query<DistrictAdminViewModel[], number>({
				query: (districtId) => {
					return {
						url: `/District/DistrictAdminListByDistrict`,
						params: {
							districtId,
						},
					};
				},
				providesTags: ["DistrictAdmins"],
			}),
			addUserToDistrict: build.mutation<void, AddRemoveUserToDistrictViewModel>({
				query: (q) => {
					const { districtId, userId } = q;
					return {
						url: `/User/AddDistrict`,
						method: "POST",
						body: { districtId, userId },
					};
				},
				invalidatesTags: ["DistrictAdmins", "UserInfo"],
			}),
			removeUserFromDistrict: build.mutation<void, AddRemoveUserToDistrictViewModel>({
				query: (q) => {
					const { districtId, userId } = q;
					return {
						url: `/User/RemoveDistrict`,
						method: "POST",
						body: { districtId, userId },
					};
				},
				invalidatesTags: ["DistrictAdmins", "UserInfo"],
			}),
			getCleverSettingsInfo: build.query<DistrictCleverInfoResult, number>({
				query: (districtId) => ({
					url: `/District/EditCleverSettings`,
					method: "GET",
					params: { districtId },
				}),
				providesTags: ["CleverSettings"],
			}),
			updateCleverSettingsInfo: build.mutation<void, UpdateDistrictCleverSettingsViewModel>({
				query: (payload) => ({
					url: `/District/EditCleverSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["Districts", "District", "CleverSettings", "UserInfo"],
			}),
			getClassLinkSettingsInfo: build.query<DistrictClassLinkInfoResult, number>({
				query: (districtId) => ({
					url: `/District/EditClassLinkSettings`,
					method: "GET",
					params: { districtId },
				}),
				providesTags: ["ClassLinkSettings"],
			}),
			updateClassLinkSettingsInfo: build.mutation<
				void,
				UpdateDistrictClassLinkSettingsViewModel
			>({
				query: (payload) => ({
					url: `/District/EditClassLinkSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["Districts", "District", "ClassLinkSettings", "UserInfo"],
			}),
		}),
	});

export const {
	useGetDistrictsQuery,
	useGetSchoolsMutation,
	useGetSchoolsByDistrictIdMutation,
	useGetDistrictPickerOptionsQuery,
	useGetSelectDistrictOptionsQuery,
	useMakeDistrictSelectionMutation,
	useGetDistrictListQuery,
	useCreateDistrictMutation,
	useGetDistrictInfoQuery,
	useEditDistrictMutation,
	useUpdateUserAvatarSettingForDistrictMutation,
	useUpdateReportSettingForDistrictMutation,
	useSchoolListByDistrictQuery,
	useAdminListByDistrictQuery,
	useAddUserToDistrictMutation,
	useRemoveUserFromDistrictMutation,
	useGetCleverSettingsInfoQuery,
	useUpdateCleverSettingsInfoMutation,
	useGetClassLinkSettingsInfoQuery,
	useUpdateClassLinkSettingsInfoMutation,
} = districtsApiSlice;
